/**
 * Hook — useOperationHistory
 *
 * Récupère l'historique des transactions du caissier connecté
 * directement depuis le CBS via :
 *   POST /api/v1/cbs-integration/users/transactions-history
 *
 * Le codeuser est extrait du AuthContext (cardData.code ou username).
 */

import { useState, useEffect, useCallback } from 'react';
import {
  CBSTransactionsHistoryGateway,
} from '../../infrastructure/adapters/CBSTransactionsHistoryGateway';
import type { HistoryTransaction } from '../../infrastructure/adapters/CardLifeHistoryGateway';
import { useAuth } from '../contexts/AuthContext';

export type { HistoryTransaction };

export interface UseOperationHistoryResult {
  /** Toutes les transactions CBS triées par date décroissante */
  transactions: HistoryTransaction[];
  /** Indicateur de chargement */
  loading: boolean;
  /** Erreur éventuelle */
  error: string | null;
  /** Rafraîchir les données */
  refresh: () => Promise<void>;
}

// Singleton du gateway
const gateway = new CBSTransactionsHistoryGateway();

export function useOperationHistory(): UseOperationHistoryResult {
  const { user } = useAuth();
  const codeuser = user?.cardData?.code || user?.username || '';

  const [transactions, setTransactions] = useState<HistoryTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!codeuser) {
      setError('Code agent indisponible — veuillez vous reconnecter');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const all = await gateway.fetchHistory(codeuser);
      setTransactions(all);
      console.log(`[useOperationHistory] ${all.length} transactions CBS chargées pour ${codeuser}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur lors du chargement de l\'historique';
      console.error('[useOperationHistory]', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [codeuser]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    transactions,
    loading,
    error,
    refresh: load,
  };
}
